/**
 * useChunkedRecorder — continuous audio capture in independently decodable
 * segments, for the bench stat tracker.
 * 2026-09-19 (per Gilles, Phase 7B v1).
 *
 * WHY ROTATE INSTEAD OF SLICING. MediaRecorder.start(timeslice) looks like
 * the obvious way to get chunks, but it is a trap here: only the first blob
 * carries the container header, so every later blob is undecodable on its
 * own. A transcription service handed blob #7 gets garbage. Instead we stop
 * the recorder and immediately start a new one, so every chunk is a complete,
 * standalone audio file. The gap is the few milliseconds between stop and
 * start — shorter than a syllable, and far safer than the alternative of
 * overlapping recorders, which would duplicate words and risk double-counting
 * events downstream.
 *
 * FOREGROUND ONLY (v1). A webview's MediaRecorder dies when iOS locks the
 * screen or the user switches apps. We hold a screen wake lock so the phone
 * does not sleep on its own, and we detect and COUNT backgrounding rather
 * than pretending it did not happen — the session reports its gaps so nobody
 * trusts a transcript with holes in it. True background capture needs the
 * native plugin (Phase 7B v2); this hook's contract is designed so that
 * plugin can replace it without the page changing.
 *
 * UPLOADS never block recording. Failed chunks go into a retry queue and are
 * re-sent with backoff; recording continues regardless. Losing the network
 * on arena wifi must not lose the game.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// Ordered by preference: Opus is far better per byte for speech. Safari will
// reject all the webm/ogg options and land on mp4/AAC, which is fine.
const PREFERRED_TYPES = [
  'audio/webm;codecs=opus',
  'audio/ogg;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/aac',
];

export function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const t of PREFERRED_TYPES) {
    try { if (MediaRecorder.isTypeSupported(t)) return t; } catch { /* keep looking */ }
  }
  return '';   // '' = let the browser choose its own default
}

const baseMime = (t) => String(t || '').split(';')[0].trim().toLowerCase() || 'audio/webm';

export default function useChunkedRecorder({
  rotateMs = 20000,
  onChunk,                  // async (blob, { seq, mime, offsetMs, durationMs }) => void
  onGap,                    // (reason) => void
} = {}) {
  const [state, setState] = useState('idle');   // idle | starting | recording | paused | stopping | error
  const [error, setError] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [chunksSent, setChunksSent] = useState(0);
  const [pending, setPending] = useState(0);
  const [gapCount, setGapCount] = useState(0);
  // 2026-10-01 — the mic level is NOT React state any more. It used to be
  // set from a requestAnimationFrame loop, i.e. the whole live page (score,
  // buttons, player grid, toasts) re-rendered ~60 times a second for the
  // entire game. On a phone that is what "froze" the tracker mid-game: the
  // main thread fell behind, and the score's +/- buttons were being replaced
  // between a finger's touchstart and touchend, so taps were lost. The meter
  // now reads `levelRef` itself (see MicLevel in LiveTrackingPage) and no
  // component re-renders for it.
  const levelRef = useRef(0);                   // 0..1 mic level, for the UI meter

  const streamRef   = useRef(null);
  const recRef      = useRef(null);
  const rotateRef   = useRef(null);
  const seqRef      = useRef(0);
  const startedRef  = useRef(0);
  const chunkStart  = useRef(0);
  const stoppingRef = useRef(false);
  const wakeRef     = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef      = useRef(null);
  const queueRef    = useRef([]);
  const drainingRef = useRef(false);
  // 2026-09-23 — pause support. Offsets and the clock exclude paused time so
  // the engine sees one contiguous recording, not a silence gap.
  const pausedRef     = useRef(false);
  const pausedAtRef   = useRef(0);
  const pausedTotalRef= useRef(0);
  const mimeRef       = useRef(undefined);
  const onChunkRef  = useRef(onChunk);
  const onGapRef    = useRef(onGap);

  useEffect(() => { onChunkRef.current = onChunk; }, [onChunk]);
  useEffect(() => { onGapRef.current = onGap; }, [onGap]);

  // ── upload queue: retries with backoff, never blocks capture
  const drain = useCallback(async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    try {
      while (queueRef.current.length > 0) {
        const item = queueRef.current[0];
        try {
          await onChunkRef.current?.(item.blob, item.meta);
          queueRef.current.shift();
          setPending(queueRef.current.length);
          setChunksSent(n => n + 1);
        } catch (e) {
          item.tries = (item.tries || 0) + 1;
          if (item.tries >= 6) {
            // Give up on this one so a permanently-bad chunk cannot block
            // every chunk behind it. The audio is lost; the session records
            // it as a gap so the transcript is not silently trusted.
            queueRef.current.shift();
            setPending(queueRef.current.length);
            setGapCount(n => n + 1);
            onGapRef.current?.('upload-failed');
          } else {
            await new Promise(r => setTimeout(r, Math.min(30000, 1000 * 2 ** item.tries)));
          }
        }
      }
    } finally {
      drainingRef.current = false;
    }
  }, []);

  const enqueue = useCallback((blob, meta) => {
    if (!blob || blob.size === 0) return;
    queueRef.current.push({ blob, meta, tries: 0 });
    setPending(queueRef.current.length);
    drain();
  }, [drain]);

  // ── one recorder generation; its onstop starts the next
  const startGeneration = useCallback((mimeType) => {
    const stream = streamRef.current;
    if (!stream) return;
    let rec;
    try {
      rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch (e) {
      setError('This device cannot record audio in the browser.');
      setState('error');
      return;
    }
    recRef.current = rec;
    const seq = seqRef.current++;
    const offsetMs = Date.now() - startedRef.current - pausedTotalRef.current;
    chunkStart.current = Date.now();
    const parts = [];

    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) parts.push(e.data); };
    rec.onerror = () => { setError('Recording error — trying to continue.'); };
    // 2026-09-30 — when the OS takes the mic (phone locked, a call, another
    // app), the track ends and the recorder stops for good; before, the page
    // then showed "Start" beside a still-open server session. Now the loss is
    // counted as a gap and the tracker is told to tap Start again.
    const track = stream.getAudioTracks()[0];
    if (track && !track._gdEnded) {
      track._gdEnded = true;
      track.addEventListener('ended', () => {
        if (stoppingRef.current) return;
        setGapCount(n => n + 1);
        onGapRef.current?.('mic-lost');
        setError('The microphone was taken away (phone locked or another app used it). Tap the mic to start again.');
        setState('error');
      });
    }
    rec.onstop = () => {
      const durationMs = Date.now() - chunkStart.current;
      const type = baseMime(rec.mimeType || mimeType);
      if (parts.length > 0) {
        enqueue(new Blob(parts, { type }), { seq, mime: type, offsetMs, durationMs });
      }
      // Immediately begin the next generation unless we are shutting down
      // (or the track has ended — a new recorder on a dead stream throws).
      const alive = streamRef.current?.getAudioTracks().some(t => t.readyState === 'live');
      if (!stoppingRef.current && !pausedRef.current && alive) startGeneration(mimeType);
    };

    rec.start();
    rotateRef.current = setTimeout(() => {
      try { if (rec.state !== 'inactive') rec.stop(); } catch { /* already gone */ }
    }, rotateMs);
  }, [enqueue, rotateMs]);

  // Returns true when the mic is live, false when it is not — the page must
  // not open a server session on a mic that never started (2026-09-30: it
  // checked `rec.state` right after awaiting start(), which is the value from
  // the render BEFORE the click, so a denied mic still opened a session).
  const start = useCallback(async () => {
    if (state === 'recording' || state === 'starting' || state === 'paused') return state !== 'error';
    setError(null);
    setState('starting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          // Arena noise with a close talker: the platform's own voice
          // processing helps more than it hurts. Worth A/B testing against
          // raw capture once we have transcripts to compare.
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
      streamRef.current = stream;
      seqRef.current = 0;
      startedRef.current = Date.now();
      stoppingRef.current = false;
      pausedRef.current = false; pausedTotalRef.current = 0;
      setChunksSent(0); setGapCount(0); setElapsedMs(0);

      // Screen wake lock so the phone does not sleep mid-game. Not supported
      // everywhere; absence is not fatal, it just means the tracker has to
      // keep the screen on themselves.
      try {
        if (navigator.wakeLock?.request) wakeRef.current = await navigator.wakeLock.request('screen');
      } catch { /* non-fatal */ }

      // Cheap level meter so the tracker can see it is actually hearing them.
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteTimeDomainData(buf);
          let peak = 0;
          for (let i = 0; i < buf.length; i++) peak = Math.max(peak, Math.abs(buf[i] - 128));
          levelRef.current = Math.min(1, peak / 90);
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch { /* meter is cosmetic */ }

      mimeRef.current = pickMimeType();
      if (stoppingRef.current) {
        // Stop was pressed while the permission prompt was up: let go again.
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        setState('idle');
        return false;
      }
      startGeneration(mimeRef.current);
      setState('recording');
      return true;
    } catch (e) {
      setError(e?.name === 'NotAllowedError'
        ? 'Microphone permission was denied.'
        : 'Could not start the microphone.');
      setState('error');
      return false;
    }
  }, [state, startGeneration]);

  // Pause: flush the current chunk, mute the track (the browser's red
  // "recording" indicator goes idle), keep the stream and wake lock so
  // resume is instant and needs no new permission prompt.
  const pause = useCallback(() => {
    if (state !== 'recording') return;
    pausedRef.current = true;
    pausedAtRef.current = Date.now();
    clearTimeout(rotateRef.current);
    try { if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop(); } catch { /* ignore */ }
    streamRef.current?.getAudioTracks().forEach(t => { t.enabled = false; });
    setState('paused');
  }, [state]);

  const resume = useCallback(() => {
    if (state !== 'paused' || !streamRef.current) return;
    pausedTotalRef.current += Date.now() - pausedAtRef.current;
    pausedRef.current = false;
    streamRef.current.getAudioTracks().forEach(t => { t.enabled = true; });
    startGeneration(mimeRef.current);
    setState('recording');
  }, [state, startGeneration]);

  const stop = useCallback(async () => {
    stoppingRef.current = true;
    pausedRef.current = false;
    if (state === 'paused') pausedTotalRef.current += Date.now() - pausedAtRef.current;
    setState('stopping');
    clearTimeout(rotateRef.current);
    // 2026-09-24 — wait for the recorder's own onstop so the LAST chunk is
    // queued before we drain. It used to race: drain() saw an empty queue,
    // the page told the server "stopped", and the final chunk then hit a
    // closed session (409) — up to 20 s of the game lost every time.
    const r = recRef.current;
    if (r && r.state !== 'inactive') {
      await new Promise((resolve) => {
        const prev = r.onstop;
        r.onstop = (e) => { try { prev?.(e); } finally { resolve(); } };
        try { r.stop(); } catch { resolve(); }
        setTimeout(resolve, 3000);   // a wedged recorder must not wedge Stop
      });
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { await audioCtxRef.current?.close(); } catch { /* ignore */ }
    audioCtxRef.current = null;
    try { await wakeRef.current?.release(); } catch { /* ignore */ }
    wakeRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    // Drain with a cap. With the rink's Wi-Fi refusing uploads, drain()'s
    // retry backoff (6 tries, up to 30 s each) kept Stop "frozen" for
    // minutes. Give it 8 s; whatever is still queued keeps retrying in the
    // background and the server session is closed by the caller anyway.
    await Promise.race([drain(), new Promise(r2 => setTimeout(r2, 8000))]);
    setState('idle');
    levelRef.current = 0;
  }, [drain, state]);

  // elapsed ticker
  useEffect(() => {
    if (state !== 'recording') return undefined;
    const id = setInterval(() => setElapsedMs(Date.now() - startedRef.current - pausedTotalRef.current), 1000);
    return () => clearInterval(id);
  }, [state]);

  // Backgrounding: count it honestly rather than pretending capture continued.
  useEffect(() => {
    if (state !== 'recording') return undefined;
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        setGapCount(n => n + 1);
        onGapRef.current?.('backgrounded');
      } else if (wakeRef.current === null && navigator.wakeLock?.request) {
        // Wake locks are dropped when the page is hidden; re-take it.
        navigator.wakeLock.request('screen').then(l => { wakeRef.current = l; }).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [state]);

  // Never leave the mic hot on unmount.
  useEffect(() => () => {
    stoppingRef.current = true;
    clearTimeout(rotateRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { recRef.current?.state !== 'inactive' && recRef.current?.stop(); } catch { /* ignore */ }
    streamRef.current?.getTracks().forEach(t => t.stop());
    try { wakeRef.current?.release(); } catch { /* ignore */ }
  }, []);

  return {
    state, error, elapsedMs, chunksSent, pending, gapCount, levelRef,
    isRecording: state === 'recording',
    isPaused: state === 'paused',
    supported: typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia,
    start, stop, pause, resume,
  };
}
